import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useStoresQuery, type StoreInfo } from "../lib/api/stores.js";

const STORAGE_KEY = "onshelf.selectedStoreId";

interface SelectedStoreContextValue {
  selectedStoreId: string | null;
  selectedStore: StoreInfo | null;
  setSelectedStoreId: (id: string | null) => void;
  stores: StoreInfo[];
  isLoading: boolean;
}

const SelectedStoreContext = createContext<SelectedStoreContextValue | null>(null);

function readPersisted(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePersisted(id: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore quota / disabled storage
  }
}

export function SelectedStoreProvider({ children }: { children: ReactNode }) {
  const storesQuery = useStoresQuery({ pageSize: 100, isActive: true });
  const stores = useMemo(() => storesQuery.data?.items ?? [], [storesQuery.data]);
  const [selectedStoreId, setSelectedStoreIdState] = useState<string | null>(readPersisted);

  // Auto-pick first active store if none persisted (or persisted one no longer exists).
  useEffect(() => {
    if (stores.length === 0) return;
    if (!selectedStoreId || !stores.some((s) => s.id === selectedStoreId)) {
      const fallback = stores[0]?.id ?? null;
      setSelectedStoreIdState(fallback);
      writePersisted(fallback);
    }
  }, [stores, selectedStoreId]);

  const setSelectedStoreId = useCallback((id: string | null) => {
    setSelectedStoreIdState(id);
    writePersisted(id);
  }, []);

  const selectedStore = useMemo(
    () => stores.find((s) => s.id === selectedStoreId) ?? null,
    [stores, selectedStoreId],
  );

  const value = useMemo<SelectedStoreContextValue>(
    () => ({
      selectedStoreId,
      selectedStore,
      setSelectedStoreId,
      stores,
      isLoading: storesQuery.isPending,
    }),
    [selectedStoreId, selectedStore, setSelectedStoreId, stores, storesQuery.isPending],
  );

  return (
    <SelectedStoreContext.Provider value={value}>
      {children}
    </SelectedStoreContext.Provider>
  );
}

export function useSelectedStore(): SelectedStoreContextValue {
  const ctx = useContext(SelectedStoreContext);
  if (!ctx) throw new Error("useSelectedStore must be used inside SelectedStoreProvider");
  return ctx;
}
